import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardSides, CardTemplateDto } from '../../../api/cards';
import { ErrorMessage } from '../../common/ErrorMessage';
import { FormFieldsInput } from '../FormFieldsInput';
import { TemplateStep } from './SingleCardTemplateStep';
import type {
  SingleCardForm as FormApi,
  useTemplatePick,
} from './use-single-card';

/**
 * Everything the operator fills in for one card. The steps that appear depend
 * on the chosen template: a CSV or custom one asks for every extra Liquid
 * field it uses, a student or employee one for its `form:` fields.
 */
export function SingleCardForm({
  form,
  template,
  savedTemplates,
  selectedTemplateId,
  activeTpl,
  customTemplateFile,
  source,
  sides,
  fieldValues,
  extraFields,
  formFields,
  iseeEnabled,
  luccaEnabled,
  canGenerate,
  error,
  onSubmit,
}: {
  form: FormApi;
  template: ReturnType<typeof useTemplatePick>;
  savedTemplates: CardTemplateDto[];
  selectedTemplateId: string;
  activeTpl?: CardTemplateDto;
  customTemplateFile: File | null;
  source: 'csv' | 'isee' | 'lucca';
  sides: CardSides;
  fieldValues: Record<string, string>;
  /** Every extra field the generated side(s) use — asked for on CSV templates. */
  extraFields: string[];
  /** The `form:` subset — asked for on ISEE/Lucca templates. */
  formFields: string[];
  iseeEnabled: boolean;
  luccaEnabled: boolean;
  canGenerate: boolean;
  error: string | null;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const sidesLabelId = useId();
  const resolvedSvg = template.resolvedSvg;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-3"
    >
      <TemplateStep
        savedTemplates={savedTemplates}
        selectedTemplateId={selectedTemplateId}
        resolvedSvg={resolvedSvg}
        activeTpl={activeTpl}
        customTemplateFile={customTemplateFile}
        onSelect={template.selectSaved}
        onPickFile={template.pickFile}
        onClear={template.clear}
      />

      {resolvedSvg && activeTpl?.backSide && (
        <div>
          <p
            id={sidesLabelId}
            className="mb-1 block text-xs font-medium text-ink"
          >
            {t('cardGenerator.sides.label')}
          </p>
          <div
            role="group"
            aria-labelledby={sidesLabelId}
            className="flex w-fit gap-0.5 bg-ink/5 p-0.5"
          >
            {(['both', 'front', 'back'] satisfies CardSides[]).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={sides === s}
                onClick={() => form.setFieldValue('sides', s)}
                className="px-3 py-1 text-xs font-medium transition-colors"
                style={
                  sides === s
                    ? { background: 'var(--card)', color: 'var(--ink)' }
                    : { background: 'transparent', color: 'var(--muted)' }
                }
              >
                {t(`cardGenerator.sides.${s}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      {source === 'isee' && !iseeEnabled && (
        <ErrorMessage message={t('cardGenerator.isee.disabled')} size="xs" />
      )}

      {source === 'lucca' && !luccaEnabled && (
        <ErrorMessage
          message={t('cardGenerator.employee.disabled')}
          size="xs"
        />
      )}

      {source === 'csv' && extraFields.length > 0 && (
        <FormFieldsInput
          fields={extraFields}
          values={fieldValues}
          onChange={(field, value) =>
            form.setFieldValue(`fieldValues.${field}`, value)
          }
          title={t('singleCardGenerator.additionalFields')}
        />
      )}

      {(source === 'isee' || source === 'lucca') && formFields.length > 0 && (
        <FormFieldsInput
          fields={formFields}
          values={fieldValues}
          onChange={(field, value) =>
            form.setFieldValue(`fieldValues.${field}`, value)
          }
          title={t('singleCardGenerator.additionalFields')}
        />
      )}

      <button
        type="submit"
        disabled={!canGenerate}
        className="btn-primary w-full justify-center disabled:cursor-not-allowed disabled:opacity-40"
      >
        {t('singleCardGenerator.generate')}
      </button>

      {error && <ErrorMessage message={error} size="xs" />}
    </form>
  );
}
